(() => {
  const scenes = ['studio', 'city', 'editorial'];
  const selectionStorageKey = 'le-seven-reference-looks-model-session-v3';
  const favouriteStorageKey = 'le-seven-reference-looks-favourites-v3';
  const filterDefinitions = [
    { id: 'face_shape', label: 'Face outline', options: ['Oval', 'Round', 'Angular', 'Heart / defined', 'Long'] },
    { id: 'face_width', label: 'Face width', options: ['Narrow', 'Balanced', 'Broad'] },
    { id: 'gender_expression', label: 'Gender', options: ['Feminine', 'Masculine'] },
    { id: 'skin_tone', label: 'Skin tone', options: ['Light', 'Light-medium', 'Medium', 'Medium-deep', 'Deep'] },
    { id: 'heritage_representation', label: 'Heritage representation', options: ['East Asian', 'South Asian', 'Southeast Asian', 'Black', 'Latinx / Hispanic', 'Middle Eastern / North African', 'White', 'Multiracial'] }
  ];
  const readStorage = (storage, key) => { try { return storage.getItem(key); } catch (error) { return null; } };
  const writeStorage = (storage, key, value) => { try { storage.setItem(key, value); } catch (error) { /* Optional browser preference. */ } };
  const escapeHtml = (value) => String(value || '').replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character]));
  const titleCase = (value) => value.charAt(0).toUpperCase() + value.slice(1);

  function init(section) {
    const library = window.LeSevenReferenceLooksLibrary;
    if (!library?.models?.length || !library?.colourways?.length || !library?.directLooks) return;

    const element = (selector) => section.querySelector(selector);
    const all = (selector) => Array.from(section.querySelectorAll(selector));
    const grid = element('[data-reference-model-shop-grid]');
    const productGrid = element('[data-reference-model-shop-products]');
    const filterGrid = element('[data-reference-model-shop-filters]');
    const picker = element('[data-reference-model-shop-picker]');
    const pickerToggle = element('[data-reference-model-shop-picker-toggle]');
    const pickerPanel = element('[data-reference-model-shop-picker-panel]');
    const validIds = library.models.map((model) => model.id);
    const hasFullSet = (colourway, modelId) => scenes.every((scene) => Boolean(library.directLooks?.[colourway.id]?.[modelId]?.[scene]));
    const categoryFor = (colourway) => {
      if (String(colourway.product_type || '').toLowerCase().includes('sun')) return 'sunglasses';
      if (String(colourway.product_type || '').toLowerCase().includes('eye')) return 'eyeglasses';
      return /sunglasses|\bsedona\b/.test(String(colourway.product_handle || '').toLowerCase()) ? 'sunglasses' : 'eyeglasses';
    };
    const productsFor = (modelId, category = 'all') => library.colourways
      .filter((colourway) => hasFullSet(colourway, modelId) && (category === 'all' || categoryFor(colourway) === category))
      .sort((first, second) => `${first.product_title} ${first.color}`.localeCompare(`${second.product_title} ${second.color}`));
    const availableModels = library.models.filter((model) => productsFor(model.id).length);
    if (!availableModels.length) return;

    let favourites;
    try {
      const stored = JSON.parse(readStorage(localStorage, favouriteStorageKey) || '[]');
      favourites = Array.isArray(stored) ? stored.filter((modelId) => validIds.includes(modelId)) : [];
    } catch (error) {
      favourites = [];
    }
    const storedModel = readStorage(sessionStorage, selectionStorageKey);
    let selectedId = availableModels.some((model) => model.id === storedModel) ? storedModel : availableModels[0].id;
    let category = 'all';
    let scene = 'studio';
    let filters = {};
    const isFavourite = (modelId) => favourites.includes(modelId);
    const persistFavourites = () => writeStorage(localStorage, favouriteStorageKey, JSON.stringify(favourites));
    const selectedModel = () => availableModels.find((model) => model.id === selectedId) || availableModels[0];

    function setPicker(open, { focus = false } = {}) {
      picker.classList.toggle('is-expanded', open);
      pickerPanel.hidden = !open;
      pickerToggle.setAttribute('aria-expanded', String(open));
      element('[data-reference-model-shop-picker-action]').textContent = open ? 'Close' : 'Change';
      if (focus) pickerToggle.focus({ preventScroll: true });
    }

    function ensureAvailableCategory() {
      if (productsFor(selectedId, category).length) return;
      category = productsFor(selectedId, 'all').length ? 'all' : productsFor(selectedId, 'sunglasses').length ? 'sunglasses' : 'eyeglasses';
    }

    function updatePickerSummary() {
      const model = selectedModel();
      const count = productsFor(model.id).length;
      const fitAnnotation = `${model.face_shape} — ${model.face_width}`;
      const selectedLabel = `${model.name} · ${fitAnnotation}`;
      const portrait = element('[data-reference-model-shop-summary-portrait]');
      if (portrait) {
        portrait.src = model.portrait;
        portrait.alt = '';
      }
      element('[data-reference-model-shop-summary-name]').textContent = selectedLabel;
      element('[data-reference-model-shop-summary-detail]').textContent = `${count} ${count === 1 ? 'colourway' : 'colourways'} ready to explore`;
      all('[data-reference-model-shop-name]').forEach((target) => { target.textContent = selectedLabel; });
    }

    function renderControls() {
      all('[data-reference-model-shop-category]').forEach((button) => {
        const selected = button.dataset.referenceModelShopCategory === category;
        button.setAttribute('aria-pressed', String(selected));
      });
      all('[data-reference-model-shop-scene]').forEach((button) => {
        const selected = button.dataset.referenceModelShopScene === scene;
        button.setAttribute('aria-pressed', String(selected));
      });
    }

    function renderFilters() {
      filterGrid.innerHTML = filterDefinitions.map((filter) => {
        const options = ['<option value="">Any</option>', ...filter.options.map((option) => `<option value="${escapeHtml(option)}">${escapeHtml(option)}</option>`)].join('');
        return `<label class="le-seven-reference-model-shop__filter"><span>${escapeHtml(filter.label)}</span><select data-reference-model-shop-filter="${escapeHtml(filter.id)}">${options}</select></label>`;
      }).join('');
      filterGrid.querySelectorAll('[data-reference-model-shop-filter]').forEach((select) => {
        const id = select.dataset.referenceModelShopFilter;
        select.value = filters[id] || '';
        select.addEventListener('change', () => {
          filters = { ...filters, [id]: select.value };
          renderModels();
        });
      });
    }

    function renderProducts() {
      const model = selectedModel();
      ensureAvailableCategory();
      const products = productsFor(model.id, category);
      const categoryLabel = category === 'all' ? 'All frames' : category === 'sunglasses' ? 'Sunglasses' : 'Eyeglasses';
      const sceneLabel = scene === 'all' ? 'All scenes' : titleCase(scene);
      const cards = products.flatMap((colourway) => (scene === 'all' ? scenes : [scene]).map((sceneName) => ({ colourway, sceneName })));
      element('[data-reference-model-shop-count]').textContent = `${products.length} ${products.length === 1 ? 'colourway' : 'colourways'} · ${categoryLabel} · ${sceneLabel}`;
      productGrid.innerHTML = cards.map(({ colourway, sceneName }) => {
        const image = library.directLooks[colourway.id][model.id][sceneName];
        const title = `${colourway.product_title} · ${colourway.color}`;
        return `<a class="le-seven-reference-model-shop__product-card" href="/products/${encodeURIComponent(colourway.product_handle)}#reference-looks" data-reference-model-shop-product="${escapeHtml(colourway.product_handle)}"><span class="le-seven-reference-model-shop__product-media"><img src="${escapeHtml(image)}" alt="${escapeHtml(model.name)} wearing ${escapeHtml(title)} in a ${escapeHtml(sceneName)} reference look" loading="lazy" width="1024" height="1024"></span><span class="le-seven-reference-model-shop__product-copy"><strong>${escapeHtml(colourway.product_title)}</strong><span>${escapeHtml(colourway.color)}</span><small><span class="le-seven-reference-model-shop__product-scene">${escapeHtml(titleCase(sceneName))}</span> on ${escapeHtml(model.name)} <b aria-hidden="true">↗</b></small></span></a>`;
      }).join('');
      element('[data-reference-model-shop-empty]').hidden = Boolean(products.length);
      productGrid.hidden = !products.length;
      productGrid.querySelectorAll('[data-reference-model-shop-product]').forEach((link) => link.addEventListener('click', () => writeStorage(sessionStorage, selectionStorageKey, selectedId)));
      renderControls();
    }

    function renderModels() {
      const sorted = availableModels
        .filter((model) => Object.entries(filters).every(([key, value]) => !value || model[key] === value))
        .sort((first, second) => Number(isFavourite(second.id)) - Number(isFavourite(first.id)) || first.name.localeCompare(second.name));
      grid.innerHTML = sorted.map((model) => {
        const selected = model.id === selectedId;
        const favourite = isFavourite(model.id);
        const count = productsFor(model.id).length;
        return `<article class="le-seven-reference-model-shop__model${selected ? ' is-selected' : ''}${favourite ? ' is-favourite' : ''}"><button type="button" class="le-seven-reference-model-shop__model-select" data-reference-model-shop-select="${escapeHtml(model.id)}" aria-pressed="${selected}"><span class="le-seven-reference-model-shop__model-media"><img src="${escapeHtml(model.portrait)}" alt="${escapeHtml(model.name)}, reference identity" loading="lazy" width="600" height="600"></span><span class="le-seven-reference-model-shop__model-copy"><strong>${escapeHtml(model.name)}</strong><small>${escapeHtml(model.face_shape)} · ${escapeHtml(model.face_width)}</small><em>${count} ${count === 1 ? 'colourway' : 'colourways'}</em></span></button><button type="button" class="le-seven-reference-model-shop__favourite" data-reference-model-shop-favourite="${escapeHtml(model.id)}" aria-label="${favourite ? 'Remove' : 'Save'} ${escapeHtml(model.name)} as a favourite" aria-pressed="${favourite}"><span aria-hidden="true">${favourite ? '♥' : '♡'}</span></button>${favourite ? '<span class="le-seven-reference-model-shop__saved">Saved</span>' : ''}</article>`;
      }).join('');
      element('[data-reference-model-shop-result]').textContent = `${sorted.length} ${sorted.length === 1 ? 'reference' : 'references'} available${favourites.length ? ' · saved references first' : ''}`;
      grid.hidden = !sorted.length;
      element('[data-reference-model-shop-model-empty]').hidden = Boolean(sorted.length);
      grid.querySelectorAll('[data-reference-model-shop-select]').forEach((button) => button.addEventListener('click', () => {
        selectedId = button.dataset.referenceModelShopSelect;
        writeStorage(sessionStorage, selectionStorageKey, selectedId);
        ensureAvailableCategory();
        updatePickerSummary();
        renderModels();
        renderProducts();
        setPicker(false, { focus: true });
      }));
      grid.querySelectorAll('[data-reference-model-shop-favourite]').forEach((button) => button.addEventListener('click', () => {
        const modelId = button.dataset.referenceModelShopFavourite;
        favourites = isFavourite(modelId) ? favourites.filter((id) => id !== modelId) : [...favourites, modelId];
        persistFavourites();
        renderModels();
      }));
    }

    pickerToggle.addEventListener('click', () => setPicker(!picker.classList.contains('is-expanded')));
    element('[data-reference-model-shop-reset]').addEventListener('click', () => {
      filters = {};
      renderFilters();
      renderModels();
    });
    all('[data-reference-model-shop-category]').forEach((button) => button.addEventListener('click', () => {
      category = button.dataset.referenceModelShopCategory;
      renderProducts();
    }));
    all('[data-reference-model-shop-scene]').forEach((button) => button.addEventListener('click', () => {
      scene = button.dataset.referenceModelShopScene;
      renderProducts();
    }));

    writeStorage(sessionStorage, selectionStorageKey, selectedId);
    updatePickerSummary();
    renderFilters();
    renderModels();
    renderProducts();
  }

  document.addEventListener('DOMContentLoaded', () => document.querySelectorAll('[data-reference-model-shop]').forEach(init));
})();
